import { EventItem } from "@/src/models/event.model";
import { formatUserName } from "@/src/utils/string.utils";
import { addDays, differenceInCalendarDays, format, startOfDay } from "date-fns";

/**
 * One calendar day of one event.
 *
 * A multi-day event produces one of these per day it covers, which is what lets
 * the calendar say "Day 2 of 5" on the middle cell rather than repeating the
 * same undifferentiated title on all five.
 */
export type CalendarDayEvent = Readonly<{
  event: EventItem;
  /** 1-based position of this day within the event. Always 1 for a single-day event. */
  dayNumber: number;
  /** How many calendar days the event spans. 1 for a single-day event. */
  totalDays: number;
}>;

/**
 * The `YYYY-MM-DD` key react-native-calendars uses for a day cell.
 *
 * Deliberately *local* time. This was `date.toISOString().split("T")[0]`, which
 * is UTC: west of Greenwich an evening event landed on the following day's cell,
 * and east of it a morning event landed on the previous one — so the marker sat
 * next to the day the event was not on. `date-fns/format` reads the local
 * calendar fields, which is the same frame the calendar grid itself is in.
 */
export const toCalendarDateKey = (date: Date): string =>
  format(date, "yyyy-MM-dd");

/**
 * Upper bound on the days a single event may occupy in the map.
 *
 * The expansion below walks one map insert per day, so a row with a corrupt
 * `endTime` (a stray year like 9999, which the form does not prevent) would
 * otherwise spin through millions of iterations and hang the sidebar on render.
 * A year is far past anything the product supports; beyond it the event is
 * simply truncated rather than allowed to take the UI down.
 */
const MAX_EVENT_DAYS = 366;

const isValidDate = (date: Date): boolean => !Number.isNaN(date.getTime());

/**
 * Events keyed by every calendar day they touch, each entry carrying its
 * day-within-the-event position.
 *
 * Days are sorted by start time so a cell with several events lists them in the
 * order they happen, not in whatever order the query returned.
 */
export const buildEventsByDate = (
  events: readonly EventItem[],
): Map<string, CalendarDayEvent[]> => {
  const byDate = new Map<string, CalendarDayEvent[]>();

  for (const event of events) {
    const start = new Date(event?.startTime);
    const rawEnd = new Date(event?.endTime);
    if (!isValidDate(start)) continue;

    // An absent or unparseable end is treated as a same-day event rather than
    // dropping the event off the calendar entirely.
    const end = isValidDate(rawEnd) ? rawEnd : start;

    const startDay = startOfDay(start);
    const spannedDays = differenceInCalendarDays(startOfDay(end), startDay) + 1;
    // A negative span means endTime precedes startTime — bad data, but the
    // event still exists and belongs on its start date.
    const totalDays = Math.min(Math.max(spannedDays, 1), MAX_EVENT_DAYS);

    for (let offset = 0; offset < totalDays; offset++) {
      const key = toCalendarDateKey(addDays(startDay, offset));
      const dayEvents = byDate.get(key) ?? [];
      dayEvents.push({ event, dayNumber: offset + 1, totalDays });
      byDate.set(key, dayEvents);
    }
  }

  for (const dayEvents of byDate.values()) {
    dayEvents.sort(
      (a, b) =>
        new Date(a.event.startTime).getTime() -
        new Date(b.event.startTime).getTime(),
    );
  }

  return byDate;
};

/**
 * The organiser's display name, or `""` when the event carries no snapshot.
 *
 * `CreatedBy` is deliberately not a fallback: it is a raw user id, and showing
 * a GUID where a person's name belongs is worse than showing nothing — the
 * caller omits the line instead.
 */
export const getEventOrganizerName = (event: EventItem): string =>
  formatUserName(
    event?.eventOrganizerSnapshot?.firstName,
    event?.eventOrganizerSnapshot?.lastName,
  );
